// Shared smart-search helpers used by floating-search-bar.js and search-results.liquid.
window.NkSmartSearch = (function () {
  const API_URL = 'https://s.nijikart.com/designstudio/api/smart-search/';

  const FILTER_MAP = {
    occasion:   'filter.p.m.custom.filter_occasions',
    recipient:  'filter.p.m.custom.filter_recipient',
    age_group:  'filter.p.m.custom.filter_age_group',
    interests:  'filter.p.m.custom.filter_interests'
  };

  // Fast client-side vocabulary match — avoids the API round-trip entirely for
  // common terms. Values must exactly match what's in cus_discovery_attribute.
  const INSTANT_OCCASIONS = {
    birthday: 'Birthday', bday: 'Birthday', 'b-day': 'Birthday',
    janmdin: 'Birthday', janamdin: 'Birthday', janamdin: 'Birthday',
    anniversary: 'Anniversary',
    wedding: 'Wedding',
    christmas: 'Christmas', xmas: 'Christmas',
    diwali: 'Diwali',
    graduation: 'Graduation',
    valentine: "Valentine's Day",
    mothers: "Mother's Day",
    fathers: "Father's Day",
    raksha: 'Raksha Bandhan', rakhi: 'Raksha Bandhan',
    holi: 'Holi',
  };
  const INSTANT_INTERESTS = {
    thor: 'Thor', spiderman: 'Spiderman', 'spider man': 'Spiderman',
    marvel: 'Marvel', 'harry potter': 'Harry Potter',
    'star wars': 'Star Wars', disney: 'Disney',
    pokemon: 'Pokemon', dinosaur: 'Dinosaur', dino: 'Dinosaur',
    unicorn: 'Unicorn', cat: 'Cat', dog: 'Dog',
    gaming: 'Gaming', gamer: 'Gaming',
    music: 'Music', sports: 'Sports', coding: 'Coding',
    anime: 'Anime', f1: 'F1',
  };

  /**
   * Try to match the query against known vocabulary WITHOUT calling the API.
   * Returns a /search URL instantly if matched, null if a full AI call is needed.
   */
  function buildInstantUrl(query) {
    const q = (query || '').toLowerCase().trim();
    if (!q) return null;

    let primaryAttr = null, primaryVal = null;

    for (const [kw, val] of Object.entries(INSTANT_OCCASIONS)) {
      if (q.includes(kw)) { primaryAttr = 'occasions'; primaryVal = val; break; }
    }
    if (!primaryAttr) {
      for (const [kw, val] of Object.entries(INSTANT_INTERESTS)) {
        if (q.includes(kw)) { primaryAttr = 'interests'; primaryVal = val; break; }
      }
    }
    if (!primaryAttr) return null;

    const params = new URLSearchParams();
    params.set('q', query);
    params.set('discovery_attribute', primaryAttr);
    params.set('discovery_value', primaryVal);
    return '/search?' + params.toString();
  }

  // Maps AI filter keys → cus_discovery_attribute JSON array keys (used in discovery redirect)
  const DISCOVERY_ATTR_MAP = {
    occasion:   'occasions',
    recipient:  'recipients',
    interests:  'interests',
    age_group:  'discovery_age_groups',
    themes:     'themes',
    styles:     'styles',
    emotions:   'emotions',
    personas:   'personas',
    contexts:   'contexts',
  };

  // Priority order: most specific / narrowing filter comes first
  const DISCOVERY_PRIORITY = ['occasion', 'recipient', 'personas', 'interests', 'emotions', 'contexts', 'age_group', 'themes', 'styles'];

  /**
   * Build a /search URL that uses cus_discovery_attribute metafield matching.
   * Picks the primary filter for Liquid server-side loading (discovery_attribute/value),
   * and encodes any remaining matched filters in nk_filters for JS-side AND-filtering.
   * Returns null when no taxonomy match is found (fall back to native search).
   */
  function buildDiscoveryUrl(data, originalQuery) {
    var allFilters = Object.assign({}, (data && data.filters) || {});

    // Merge boosts.interests (AI puts interests here by default)
    var boostInterests = (data && data.boosts && data.boosts.interests) || [];
    if (boostInterests.length) {
      var existing = allFilters.interests || [];
      boostInterests.forEach(function (v) { if (!existing.includes(v)) existing.push(v); });
      allFilters.interests = existing;
    }

    // Collect (discAttr, value) pairs in priority order
    var pairs = [];
    DISCOVERY_PRIORITY.forEach(function (fkey) {
      var vals = allFilters[fkey];
      if (!vals || !vals.length) return;
      var discKey = DISCOVERY_ATTR_MAP[fkey];
      if (!discKey) return;
      pairs.push([discKey, vals[0]]);
    });

    if (!pairs.length) return null;

    var q = (data && data.search_text) || originalQuery;
    var params = new URLSearchParams();
    params.set('q', q);
    params.set('discovery_attribute', pairs[0][0]);
    params.set('discovery_value', pairs[0][1]);
    if (pairs.length > 1) {
      params.set('nk_filters', pairs.slice(1).map(function (p) { return p[0] + ':' + p[1]; }).join(','));
    }

    return '/search?' + params.toString();
  }

  const FILTER_VALUE_MAP = {
    occasion:  { 'return gift': 'return-gift' },
    age_group: { kid: 'kids', child: 'kids' }
  };

  function normalizeFilterValue(key, value) {
    if (!value) return '';
    const normalized = String(value).trim().toLowerCase();
    const mapped = FILTER_VALUE_MAP[key]?.[normalized];
    return mapped || normalized;
  }

  function shouldApplyFilterValue(key, value) {
    if (!value) return false;
    const normalized = String(value).trim().toLowerCase();
    if (normalized === 'gift') return false;
    return true;
  }

  async function smartSearch(query) {
    const response = await fetch(API_URL, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query })
    });

    if (!response.ok) {
      throw new Error(`Backend error: ${response.status}`);
    }

    return response.json();
  }

  function buildShopifySearchUrl(data, originalQuery) {
    const params = new URLSearchParams();
    const finalQuery = data?.search_text || originalQuery;
    params.set('q', finalQuery);
    params.set('options[prefix]', 'last');
    if (data?.filters && typeof data.filters === 'object') {
      Object.entries(data.filters).forEach(([key, values]) => {
        const param = FILTER_MAP[key];
        if (!param) return;
        const safeValues = Array.isArray(values) ? values : [values];
        safeValues.forEach((value) => {
          if (!shouldApplyFilterValue(key, value)) return;
          const normalizedValue = normalizeFilterValue(key, value);
          if (!normalizedValue) return;
          params.append(param, normalizedValue);
        });
      });
    }
    return `/search?${params.toString()}`;
  }

  // Builds a /search URL for a single filter chip using discovery mode.
  function buildFilterChipUrl(key, value, baseQuery) {
    if (!shouldApplyFilterValue(key, value)) return null;

    // age_group chips can't use discovery_attribute — the cus_discovery_attribute
    // metafield stores values like "15 years" not "Kids", so Shopify's native filter
    // returns 0 results. Re-search with the age value prepended so the AI handles it.
    if (key === 'age_group') {
      const combinedQ = [value, baseQuery].filter(Boolean).join(' ');
      return `/search?q=${encodeURIComponent(combinedQ)}&options[prefix]=last`;
    }

    const discKey = DISCOVERY_ATTR_MAP[key];
    if (discKey) {
      // Use discovery URL for all taxonomy-matched chips
      const params = new URLSearchParams();
      if (baseQuery) params.set('q', baseQuery);
      params.set('discovery_attribute', discKey);
      params.set('discovery_value', value);
      return `/search?${params.toString()}`;
    }
    // product_types and unknown keys fall back to native filter URL
    const param = FILTER_MAP[key];
    if (!param) return null;
    const normalizedValue = normalizeFilterValue(key, value);
    if (!normalizedValue) return null;
    const params = new URLSearchParams();
    if (baseQuery) params.set('q', baseQuery);
    params.append(param, normalizedValue);
    return `/search?${params.toString()}`;
  }

  const CHIP_LABELS = {
    age: 'Age',
    age_group: 'Age',
    recipient: 'For',
    occasion: 'Occasion',
    interests: 'Theme',
    themes: 'Style',
    product_types: 'Category'
  };

  const CHIP_ICONS = {
    age: '\u{1F382}',
    age_group: '\u{1F382}',
    recipient: '\u{1F9D2}',
    occasion: '\u{1F389}',
    interests: '\u{2728}',
    themes: '\u{1F3A8}',
    product_types: '\u{1F455}'
  };

  // Shared renderer for the interpretation banner/popup used by both the floating
  // search bar and the /search results page. `ids` maps logical slots to the
  // actual element IDs in whichever markup is calling this (the two surfaces use
  // different ID prefixes so they can coexist on the same page without colliding).
  function renderInterpretationPanel(data, query, ids) {
    const show = (id) => { const el = document.getElementById(id); if (el) el.hidden = false; };
    const hide = (id) => { const el = document.getElementById(id); if (el) el.hidden = true; };
    const setText = (id, text) => { const el = document.getElementById(id); if (el) el.textContent = text; };
    const clearList = (id) => { const el = document.getElementById(id); if (el) el.innerHTML = ''; };

    if (!data) return;

    // Re-renders happen repeatedly as the user types (debounced), so always
    // reset to a blank slate first instead of appending onto stale results.
    [ids.interpreted, ids.language, ids.didYouMean, ids.corrections, ids.refineSection, ids.relatedSection]
      .forEach((id) => { if (id) hide(id); });
    if (ids.refineList) clearList(ids.refineList);
    if (ids.relatedList) clearList(ids.relatedList);

    if (ids.root) show(ids.root);

    const interpretedText = data.display_text || data.search_text || '';
    if (interpretedText && ids.interpretedText) {
      setText(ids.interpretedText, interpretedText);
      show(ids.interpreted);
    }

    if (data.language && data.language !== 'english' && ids.languageText) {
      setText(ids.languageText, data.language.charAt(0).toUpperCase() + data.language.slice(1));
      show(ids.language);
    }

    if (data.did_you_mean && ids.didYouMeanText) {
      setText(ids.didYouMeanText, data.did_you_mean);
      show(ids.didYouMean);
    }

    if (data.corrections && data.corrections.length && ids.correctionsText) {
      setText(ids.correctionsText, data.corrections.map((c) => `${c.from} → ${c.to}`).join(', '));
      show(ids.corrections);
    }

    if (ids.refineList) {
      const chipEntries = [];
      const filters = data.filters || {};
      const boosts = data.boosts || {};
      Object.entries(filters).forEach(([key, values]) => {
        (Array.isArray(values) ? values : [values]).forEach((value) => chipEntries.push({ key, value }));
      });
      if (boosts.interests) {
        boosts.interests.forEach((value) => chipEntries.push({ key: 'interests', value }));
      }

      const listEl = document.getElementById(ids.refineList);
      if (listEl && chipEntries.length) {
        const baseQuery = data.search_text || query;
        chipEntries.forEach(({ key, value }) => {
          const href = buildFilterChipUrl(key, value, baseQuery);
          if (!href) return;
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.className = `nk-intent-card nk-intent-card--${key}`;
          a.href = href;
          a.innerHTML =
            `<span class="nk-intent-card__icon">${CHIP_ICONS[key] || '✨'}</span>` +
            `<span><span class="nk-intent-card__label">${CHIP_LABELS[key] || key}</span>` +
            `<span class="nk-intent-card__value">${value}</span></span>`;
          li.appendChild(a);
          listEl.appendChild(li);
        });
        if (listEl.children.length) show(ids.refineSection);
      }
    }

    if (ids.relatedList && data.related_searches && data.related_searches.length) {
      const listEl = document.getElementById(ids.relatedList);
      if (listEl) {
        data.related_searches.forEach((phrase) => {
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.className = 'nk-related-pill';
          a.href = `/search?q=${encodeURIComponent(phrase)}&options[prefix]=last`;
          a.textContent = phrase;
          li.appendChild(a);
          listEl.appendChild(li);
        });
        show(ids.relatedSection);
      }
    }
  }

  return {
    API_URL,
    FILTER_MAP,
    DISCOVERY_ATTR_MAP,
    normalizeFilterValue,
    shouldApplyFilterValue,
    smartSearch,
    buildShopifySearchUrl,
    buildDiscoveryUrl,
    buildInstantUrl,
    buildFilterChipUrl,
    renderInterpretationPanel
  };
})();
